import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import MoodBadge from "@/components/MoodBadge";
import { timeAgo } from "@/lib/constants";

export default function TrendingStrip({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/posts/trending`, { params: { hours: 24, limit: 6, user_id: user.id } })
      .then(({ data }) => mounted && setItems(data))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [user.id]);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="rounded-2xl border p-5 sm:p-6 fade-up"
      style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      data-testid="trending-strip"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 flame-pulse" style={{ color: "var(--exhale-accent)" }} />
          <h3 className="font-display text-lg font-semibold" style={{ color: "var(--exhale-text)" }}>
            Hot right now
          </h3>
          <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--exhale-text-muted)" }}>
            · last 24h
          </span>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto -mx-1 pb-2 snap-x snap-mandatory" data-testid="trending-list">
        {items.map((p, i) => {
          const reactionTotal = Object.values(p.reactions || {}).reduce((a, b) => a + b, 0);
          return (
            <Link
              key={p.id}
              to={`/vent/${p.id}`}
              data-testid={`trending-card-${p.id}`}
              className="snap-start shrink-0 w-72 sm:w-80 rounded-xl border p-4 transition-colors group"
              style={{ background: "var(--exhale-bg)", borderColor: "var(--exhale-border)" }}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(255, 90, 54, 0.12)", color: "var(--exhale-accent-2)", border: "1px solid var(--exhale-border)" }}
                >
                  <Flame className="w-3 h-3" /> #{i + 1}
                </span>
                <MoodBadge moodId={p.mood} />
              </div>
              <p
                className="text-sm leading-snug line-clamp-3 mb-3"
                style={{ color: "var(--exhale-text)", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              >
                {p.content || "(photo vent)"}
              </p>
              <div className="flex items-center justify-between text-xs font-mono" style={{ color: "var(--exhale-text-muted)" }}>
                <span className="truncate max-w-[8rem]">{p.handle}</span>
                <span className="flex items-center gap-3">
                  <span>{reactionTotal} 🔥</span>
                  <span>{p.comment_count} 💬</span>
                </span>
              </div>
              <div
                className="mt-3 pt-3 border-t flex items-center justify-between text-[10px] font-mono uppercase"
                style={{ borderColor: "var(--exhale-border)", color: "var(--exhale-text-muted)" }}
              >
                <span>{timeAgo(p.created_at)}</span>
                <span className="inline-flex items-center gap-1 group-hover:text-[color:var(--exhale-accent)] transition-colors">
                  Open <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
